import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Loader2, ArrowLeft, Send, Plus, X, Search, CheckCircle2,
  ChevronRight, ChevronDown, UserPlus,
} from 'lucide-react'
import { apiGet, cachedGet, postJson } from '../../lib/api.js'
import { nameInitial } from '../../lib/text.js'
import { relabelBranch } from '../../lib/asset.js'
import { isOnProbation } from '../../lib/employee.js'
import ConfirmDialog from '../ConfirmDialog.jsx'
import SearchableSelect from '../SearchableSelect.jsx'
import {
  buildGrid, gridToAssignments, gridTotals, chipKey,
  relationLabel, TONE_CLASS, customTargetIndex,
} from '../../lib/evaluation.js'

// The assignment screen: pick who is evaluated, then review who evaluates them.
//
// The grid is built ENTIRELY on the client from data this page already has — the employee
// roster and the rule matrix. The only thing it asks the server for is the `doctor` column,
// because "which doctors actually worked at this branch during the period" lives in the
// appointment roster and nothing else can answer it.
//
// That split is the whole reason editing feels instant: no round-trip per tick. The server
// still has the last word — POST /dispatch re-reads the rules, snapshots the forms, and
// skips any (subject, evaluator) pair that already has a live task, so re-sending a grid
// adds late evaluators without duplicating or wiping anyone's work.

const FIELD = 'w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-[13.5px] text-slate-800 placeholder:text-slate-400 outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-500/10'

const arr = (x) => (Array.isArray(x) ? x : (x?.items || []))

// Bucket a row's candidates by their `group` (the employee's position), biggest first —
// same ordering rule as step 1 so the two screens read the same way.
function groupChips(chips) {
  const m = new Map()
  for (const c of chips) {
    const g = c.group || 'อื่นๆ'
    if (!m.has(g)) m.set(g, [])
    m.get(g).push(c)
  }
  return [...m.entries()].sort((a, b) => (b[1].length - a[1].length) || a[0].localeCompare(b[0], 'th'))
}

function Chip({ c, on, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={c.hint}
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[12.5px] font-medium border transition-colors ${
        on
          ? (c.manual
            // Hand-picked rather than proposed by the rules — worth being able to tell apart
            // when re-reading a grid, since it is the one thing on screen that no rule explains.
            ? 'border-violet-400 bg-violet-50 text-violet-700'
            : 'border-brand-400 bg-brand-50 text-brand-700')
          : 'border-slate-200 text-slate-400 hover:bg-slate-50'
      }`}
    >
      {c.manual && <UserPlus size={10} strokeWidth={2.5} />}
      {c.label}
      {on ? <X size={11} strokeWidth={2.5} /> : <Plus size={11} strokeWidth={2.5} />}
    </button>
  )
}

export default function EvaluationAssignWizard({ cycle, onBack }) {
  const [step, setStep] = useState(1)
  const [employees, setEmployees] = useState([])
  const [rules, setRules] = useState([])
  const [doctorsByBranch, setDoctorsByBranch] = useState({})
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')

  const [picked, setPicked] = useState(() => new Set())
  const [search, setSearch] = useState('')
  const [branchFilter, setBranchFilter] = useState(0)
  const [probationOnly, setProbationOnly] = useState(false)
  const [collapsed, setCollapsed] = useState(() => new Set())
  const [grid, setGrid] = useState([])
  const [sending, setSending] = useState(false)
  const [confirm, setConfirm] = useState(false)
  const [done, setDone] = useState(null)

  const [branches, setBranches] = useState([])

  useEffect(() => {
    let cancelled = false
    Promise.all([
      cachedGet('/api/hr/employees', { per_page: 500, active: 'true' }),
      apiGet('/api/hr/evaluation/rule'),
      cachedGet('/api/branches'),
    ])
      .then(([e, r, b]) => {
        if (cancelled) return
        setEmployees(arr(e))
        setRules(arr(r))
        setBranches(arr(b))
      })
      .catch((x) => { if (!cancelled) setErr(x?.message || 'โหลดข้อมูลไม่สำเร็จ') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [])

  // Only positions the matrix actually covers can be evaluated — anyone else would generate
  // an empty row, so they are shown greyed with the reason rather than silently missing.
  const coveredPositions = useMemo(
    () => new Set(rules.filter((r) => r.active !== false).map((r) => r.subject_position)),
    [rules],
  )

  // The round's own scope comes first and is not negotiable — a branch-scoped round can only
  // ever evaluate that branch's staff. The branch DROPDOWN below is a second, narrowing
  // filter on top, and is only useful (and only shown) on an all-branches round.
  const inScope = useMemo(
    () => employees.filter((e) =>
      e.active !== false && (!cycle.branch_id || e.branch_id === cycle.branch_id)),
    [employees, cycle.branch_id],
  )

  // Branch + search applied, but NOT the probation toggle — so the toggle's own count can
  // say how many it would reveal without being changed by its own state.
  const searched = useMemo(() => {
    const q = search.trim().toLowerCase()
    return inScope.filter((e) => {
      if (branchFilter && e.branch_id !== branchFilter) return false
      if (!q) return true
      return [e.name, e.nickname, e.code].filter(Boolean).some((s) => String(s).toLowerCase().includes(q))
    })
  }, [inScope, search, branchFilter])

  const onProbationCount = useMemo(
    () => searched.filter((e) => isOnProbation(e)).length,
    [searched, isOnProbation],
  )

  // Probation is a FILTER, not a bulk action: narrow the list to the people still on
  // probation, then use เลือกทั้งหมดที่แสดง on the result. That composes with branch and
  // search instead of fighting them, and — unlike a select-everyone button — what you are
  // about to pick stays on screen where you can check it.
  const candidates = useMemo(
    () => (probationOnly ? searched.filter((e) => isOnProbation(e)) : searched),
    [searched, probationOnly, isOnProbation],
  )

  // Only branches that actually have staff in scope — an empty branch in the list is just a
  // way to reach a blank screen.
  const branchOptions = useMemo(() => {
    const counts = new Map()
    for (const e of inScope) counts.set(e.branch_id, (counts.get(e.branch_id) || 0) + 1)
    return [...counts.entries()]
      .map(([id, n]) => {
        const b = branches.find((x) => Number(x.id) === Number(id))
        return { id, n, label: b ? relabelBranch(b.name) : `สาขา #${id}` }
      })
      .sort((a, b) => Number(a.id) - Number(b.id))
  }, [inScope, branches])

  // Grouped by position, biggest group first — the long tail of one-person positions
  // (Graphic, Messenger, แม่บ้าน) sinks to the bottom where it belongs instead of pushing
  // the twenty ผู้ช่วย off the first screen.
  const groups = useMemo(() => {
    const m = new Map()
    for (const e of candidates) {
      const p = e.position || 'ไม่ระบุตำแหน่ง'
      if (!m.has(p)) m.set(p, [])
      m.get(p).push(e)
    }
    return [...m.entries()]
      .map(([position, items]) => ({
        position,
        items: items.sort((a, b) => String(a.nickname || a.name).localeCompare(String(b.nickname || b.name), 'th')),
        covered: coveredPositions.has(position),
      }))
      .sort((a, b) => (b.items.length - a.items.length)
        || a.position.localeCompare(b.position, 'th'))
  }, [candidates, coveredPositions])

  const branchNameOf = useCallback((id) => {
    const b = branches.find((x) => Number(x.id) === Number(id))
    return b ? relabelBranch(b.name) : `สาขา #${id}`
  }, [branches])

  const branchLabel = useMemo(
    () => branchNameOf(cycle.branch_id),
    [branchNameOf, cycle.branch_id],
  )

  function pickAllShown() {
    const next = new Set(picked)
    for (const e of candidates) if (coveredPositions.has(e.position)) next.add(e.id)
    setPicked(next)
  }

  // Select / deselect a whole position group. Uncovered positions are skipped either way —
  // ticking someone the matrix has no rule for would produce a subject with no evaluators.
  function toggleGroup(group, on) {
    const next = new Set(picked)
    for (const e of group.items) {
      if (!coveredPositions.has(e.position)) continue
      if (on) next.add(e.id)
      else next.delete(e.id)
    }
    setPicked(next)
  }

  function toggleOne(id) {
    const next = new Set(picked)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    setPicked(next)
  }

  const buildStep2 = useCallback(async () => {
    setErr('')
    const subjects = employees.filter((e) => picked.has(e.id))
    const branchIds = [...new Set(subjects.map((s) => s.branch_id).filter(Boolean))]

    // Does any rule in play actually want doctors? Only then is the roster scan worth it.
    const wantsDoctor = subjects.some((s) =>
      rules.some((r) => r.active !== false && r.subject_position === s.position && r.relation === 'doctor'))

    let docs = {}
    if (wantsDoctor) {
      setLoading(true)
      try {
        const results = await Promise.all(branchIds.map((b) =>
          apiGet(`/api/hr/evaluation/cycle/${cycle.id}/doctor-candidates`, { branch_id: b })
            .then((r) => [b, r?.candidates || []])
            .catch(() => [b, []])))
        docs = Object.fromEntries(results)
      } finally {
        setLoading(false)
      }
    }

    setDoctorsByBranch(docs)
    setGrid(buildGrid({ subjects, rules, employees, doctorsByBranch: docs }))
    setStep(2)
  }, [employees, picked, rules, cycle.id])

  // Replace one cell's selection wholesale. Everything below (single chip, whole group,
  // the searchable multi-select) funnels through this.
  function setCell(si, ri, nextSet) {
    setGrid((prev) => prev.map((row, i) => {
      if (i !== si) return row
      return {
        ...row,
        rows: row.rows.map((r, j) => (j === ri ? { ...r, selected: nextSet } : r)),
      }
    }))
  }

  function toggleChip(si, ri, key, current) {
    const next = new Set(current)
    if (next.has(key)) next.delete(key)
    else next.add(key)
    setCell(si, ri, next)
  }

  // The กำหนดเอง row is the single place to hand-pick evaluators the rules would never have
  // proposed — typically colleagues at ANOTHER branch who genuinely worked with this person
  // (a transfer, a long cover shift, a cross-branch project). buildGrid proposes only
  // same-branch peers, which is right by default; this is the escape hatch.
  //
  // Unlike every other row, its candidate list is BUILT from the selection rather than
  // filtered by it — whoever you pick becomes that row's candidates. So one multi-select is
  // the whole control: choosing adds, unchoosing removes.
  // Replace the hand-picked members of a row, leaving everything the rules proposed alone.
  // Manual entries are tagged `manual: true`, so they can be swapped out wholesale without
  // disturbing the proposed candidates sitting beside them in the same row.
  function setManualFor(si, ri, nextKeys) {
    const chosen = [...nextKeys]
      .map((k) => employees.find((x) => `employee:${x.id}` === k))
      .filter(Boolean)
    setGrid((prev) => prev.map((row, i) => {
      if (i !== si) return row
      return {
        ...row,
        rows: row.rows.map((r, j) => {
          if (j !== ri) return r
          const proposed = r.candidates.filter((c) => !c.manual)
          const proposedKeys = new Set(proposed.map(chipKey))
          const next = new Set([...r.selected].filter((k) => proposedKeys.has(k)))
          chosen.forEach((e) => next.add(`employee:${e.id}`))
          return {
            ...r,
            candidates: [
              ...proposed,
              ...chosen.map((e) => ({
                kind: 'employee',
                id: e.id,
                label: e.code || e.nickname || e.name || `#${e.id}`,
                hint: `${e.position || '—'} · ${branchNameOf(e.branch_id)}`,
                group: e.position || 'ไม่ระบุตำแหน่ง',
                manual: true,
              })),
            ],
            selected: next,
          }
        }),
      }
    }))
  }

  function toggleChipGroup(si, ri, current, chips, on) {
    const next = new Set(current)
    for (const c of chips) {
      if (on) next.add(chipKey(c))
      else next.delete(chipKey(c))
    }
    setCell(si, ri, next)
  }

  async function dispatch() {
    setConfirm(false)
    setSending(true)
    setErr('')
    try {
      const assignments = gridToAssignments(grid)
      const res = await postJson(`/api/hr/evaluation/cycle/${cycle.id}/dispatch`, {
        assignments,
        open_cycle: true,
      })
      setDone(res)
    } catch (e) {
      setErr(e?.message || 'ส่งแบบประเมินไม่สำเร็จ')
    } finally {
      setSending(false)
    }
  }

  const totals = gridTotals(grid)

  if (done) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white px-6 py-14 text-center">
        <CheckCircle2 size={40} className="text-emerald-500 mx-auto mb-4" strokeWidth={1.5} />
        <h3 className="text-[17px] font-semibold text-slate-900">ส่งแบบประเมินแล้ว</h3>
        <p className="text-[13.5px] text-slate-500 mt-1.5">
          สร้างใหม่ {done.created} ชุด
          {done.skipped > 0 && ` · ข้าม ${done.skipped} ชุด (มีอยู่แล้ว)`}
        </p>
        <p className="text-[13px] text-slate-400 mt-1">
          ผู้ประเมินจะเห็นในเมนู “ประเมิน” ของตัวเอง · ทันตแพทย์จะเห็นใน Doctor Portal
        </p>
        <button
          type="button"
          onClick={onBack}
          className="mt-6 px-4 py-2.5 rounded-xl text-[14px] font-semibold text-white bg-brand-600 hover:bg-brand-700 shadow-sm"
        >
          กลับไปรายการรอบประเมิน
        </button>
      </div>
    )
  }

  return (
    <div>
      <button
        type="button"
        onClick={step === 2 ? () => setStep(1) : onBack}
        className="inline-flex items-center gap-1.5 text-[13px] text-slate-500 hover:text-slate-900 mb-3"
      >
        <ArrowLeft size={14} strokeWidth={1.75} />
        {step === 2 ? 'กลับไปเลือกผู้ถูกประเมิน' : 'กลับไปรายการรอบประเมิน'}
      </button>

      <div className="flex items-baseline justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h2 className="text-[18px] font-semibold text-slate-900 tracking-tight">{cycle.title}</h2>
          <p className="text-[13px] text-slate-500 mt-0.5">
            {step === 1
              ? 'ขั้นที่ 1 — เลือกพนักงานที่จะถูกประเมิน'
              : 'ขั้นที่ 2 — ตรวจรายชื่อผู้ประเมิน แล้วส่ง'}
            {' · '}
            <span className="text-slate-600">
              {cycle.branch_id ? `เฉพาะสาขา ${branchLabel}` : 'ทุกสาขา'}
            </span>
          </p>
        </div>
        {step === 1 && (
          <button
            type="button"
            disabled={!picked.size || loading}
            onClick={buildStep2}
            className="px-4 py-2.5 rounded-xl text-[14px] font-semibold text-white bg-brand-600 hover:bg-brand-700 shadow-sm disabled:opacity-40 inline-flex items-center gap-2"
          >
            {loading ? <Loader2 size={15} className="animate-spin" /> : null}
            ถัดไป ({picked.size} คน)
          </button>
        )}
      </div>

      {err && <div className="text-[13px] text-rose-500 mb-3">{err}</div>}

      {step === 1 && (
        <>
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                className={`${FIELD} pl-9`}
                placeholder="ค้นหาชื่อ / ชื่อเล่น / รหัส"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            {/* Only on an all-branches round. When the round is already scoped to one branch
                this dropdown could only ever hold that same branch. */}
            {!cycle.branch_id && (
              <select
                className={`${FIELD} w-auto`}
                value={branchFilter}
                onChange={(e) => setBranchFilter(Number(e.target.value))}
              >
                <option value={0}>ทุกสาขา ({inScope.length})</option>
                {branchOptions.map((b) => (
                  <option key={b.id} value={b.id}>{b.label} ({b.n})</option>
                ))}
              </select>
            )}

            <button type="button" onClick={pickAllShown} className="px-3 py-2 rounded-xl text-[13px] font-medium text-slate-600 border border-slate-200 hover:bg-slate-50">
              เลือกทั้งหมดที่แสดง
            </button>
            <button
              type="button"
              onClick={() => setProbationOnly((v) => !v)}
              disabled={!probationOnly && !onProbationCount}
              title="กรองเฉพาะคนที่วันผ่านทดลองงานยังมาไม่ถึง (วันนี้หรือหลังจากนี้)"
              className={`px-3 py-2 rounded-xl text-[13px] font-medium border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                probationOnly
                  ? 'text-white bg-amber-500 border-amber-500 hover:bg-amber-600'
                  : 'text-amber-700 bg-amber-50 border-amber-200 hover:bg-amber-100'
              }`}
            >
              อยู่ระหว่างทดลองงาน ({onProbationCount})
            </button>
            {picked.size > 0 && (
              <button type="button" onClick={() => setPicked(new Set())} className="px-3 py-2 rounded-xl text-[13px] font-medium text-slate-400 hover:text-rose-600">
                ล้างที่เลือก ({picked.size})
              </button>
            )}
          </div>

          {loading && (
            <div className="rounded-2xl border border-slate-200 bg-white px-4 py-12 text-center text-slate-400">
              <Loader2 size={16} className="animate-spin inline mr-2" />กำลังโหลด…
            </div>
          )}

          {!loading && !groups.length && (
            // Say WHY it is empty. The commonest cause is a scope that leaves nobody —
            // which looks identical to a broken search unless it is spelled out.
            <div className="rounded-2xl border border-slate-200 bg-white px-6 py-12 text-center">
              <p className="text-[13.5px] text-slate-500">ไม่พบพนักงานตามเงื่อนไข</p>
              <p className="text-[12.5px] text-slate-400 mt-1.5">
                {cycle.branch_id ? (
                  <>
                    รอบนี้จำกัดเฉพาะ <span className="text-slate-600">สาขา{branchLabel}</span>
                    {' '}(มีพนักงาน {inScope.length} คน)
                    <br />ถ้าต้องการประเมินข้ามสาขา ให้สร้างรอบใหม่แบบ “ทุกสาขา”
                  </>
                ) : probationOnly
                  ? 'ไม่มีใครอยู่ระหว่างทดลองงานตามตัวกรองนี้ — กดปุ่ม “อยู่ระหว่างทดลองงาน” อีกครั้งเพื่อดูทุกคน'
                  : 'ลองล้างคำค้นหรือตัวกรองสาขา'}
              </p>
            </div>
          )}

          {/* Grouped by position, largest group first. Each group can be ticked or cleared in
              one go — the common case is "every ผู้ช่วย at this branch", not a hand-picked
              set, and making that one click removes most of the tedium. */}
          <div className="space-y-2.5">
            {!loading && groups.map((g) => {
              const selectable = g.covered ? g.items : []
              const nSel = selectable.filter((e) => picked.has(e.id)).length
              const allOn = selectable.length > 0 && nSel === selectable.length
              const isCollapsed = collapsed.has(g.position)
              return (
                <div key={g.position} className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
                  <div className={`flex items-center gap-3 px-4 py-2.5 border-b border-slate-100 ${g.covered ? 'bg-slate-50/60' : 'bg-slate-50/30'}`}>
                    <button
                      type="button"
                      onClick={() => setCollapsed((prev) => {
                        const next = new Set(prev)
                        if (next.has(g.position)) next.delete(g.position)
                        else next.add(g.position)
                        return next
                      })}
                      className="p-0.5 rounded text-slate-400 hover:text-slate-700"
                      title={isCollapsed ? 'ขยาย' : 'ย่อ'}
                    >
                      {isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                    </button>

                    <span className={`text-[13.5px] font-semibold ${g.covered ? 'text-slate-800' : 'text-slate-400'}`}>
                      {g.position}
                    </span>
                    <span className="text-[11.5px] text-slate-400 tabular-nums">{g.items.length} คน</span>

                    {nSel > 0 && (
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ${TONE_CLASS.emerald}`}>
                        เลือก {nSel}
                      </span>
                    )}

                    {g.covered ? (
                      <button
                        type="button"
                        onClick={() => toggleGroup(g, !allOn)}
                        className="ml-auto px-2.5 py-1 rounded-lg text-[12.5px] font-medium text-brand-700 hover:bg-brand-50"
                      >
                        {allOn ? 'ยกเลิกทั้งกลุ่ม' : 'เลือกทั้งกลุ่ม'}
                      </button>
                    ) : (
                      <span className="ml-auto text-[11.5px] text-slate-400">ยังไม่มีเกณฑ์สำหรับตำแหน่งนี้</span>
                    )}
                  </div>

                  {!isCollapsed && (
                    <div>
                      {g.items.map((e) => {
                        const on = picked.has(e.id)
                        return (
                          <label
                            key={e.id}
                            className={`flex items-center gap-3 px-4 py-2.5 border-b border-slate-50 last:border-b-0 ${
                              g.covered ? 'cursor-pointer hover:bg-brand-50/40' : 'opacity-40 cursor-not-allowed'
                            }`}
                          >
                            <input
                              type="checkbox"
                              className="accent-brand-600"
                              disabled={!g.covered}
                              checked={on}
                              onChange={() => toggleOne(e.id)}
                            />
                            <div className="w-7 h-7 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center overflow-hidden flex-none ring-1 ring-slate-200/70">
                              {e.image
                                ? <img src={e.image} alt="" className="w-full h-full object-cover" />
                                : <span className="text-[11px] font-semibold">{nameInitial(e.nickname || e.name)}</span>}
                            </div>
                            <span className="text-[13.5px] text-slate-800">{e.nickname || e.name}</span>
                            <span className="text-[12px] text-slate-400 truncate">{e.name}</span>
                            {!cycle.branch_id && (
                              <span className="text-[11.5px] text-slate-400 flex-none">
                                {branchOptions.find((b) => b.id === e.branch_id)?.label || ''}
                              </span>
                            )}
                            {isOnProbation(e) && (
                              <span
                                title={`ครบกำหนดทดลองงาน ${e.date_probationpass}`}
                                className={`ml-auto inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 flex-none ${TONE_CLASS.amber}`}
                              >
                                ทดลองงาน · ถึง {e.date_probationpass}
                              </span>
                            )}
                          </label>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </>
      )}

      {step === 2 && (
        <>
          <div className="space-y-3">
            {grid.map((row, si) => (
              // NOT overflow-hidden: the pickers inside open an absolutely-positioned list,
              // and clipping it to the card is what cut the ทันตแพทย์ dropdown down to a
              // single visible row. The header keeps its own rounding instead.
              <div key={row.subject.id} className="rounded-2xl border border-slate-200 bg-white">
                <div className="px-5 py-3 border-b border-slate-100 bg-slate-50/60 rounded-t-2xl flex items-center gap-2.5">
                  <div className="w-7 h-7 rounded-lg bg-white text-slate-500 flex items-center justify-center overflow-hidden flex-none ring-1 ring-slate-200">
                    {row.subject.image
                      ? <img src={row.subject.image} alt="" className="w-full h-full object-cover" />
                      : <span className="text-[11px] font-semibold">{nameInitial(row.subject.nickname || row.subject.name)}</span>}
                  </div>
                  <span className="text-[13.5px] font-semibold text-slate-800">{row.subject.nickname || row.subject.name}</span>
                  <span className="text-[12px] text-slate-400">{row.subject.position}</span>
                </div>

                {!row.rows.length && (
                  <div className="px-5 py-4 text-[13px] text-slate-400">
                    ยังไม่มีเกณฑ์สำหรับตำแหน่งนี้ — ตั้งค่าที่แท็บ “เกณฑ์”
                  </div>
                )}

                {row.rows.map((r, ri) => {
                  const n = r.candidates.filter((c) => r.selected.has(chipKey(c))).length
                  return (
                    <div key={r.relation} className="px-5 py-3 border-b border-slate-50 last:border-b-0">
                      {/* No "n/min คน" badge here. min_evaluators is a soft hint on the rule,
                          not a quota — nothing enforces it and the round sends regardless — so
                          rendering "0/3 คน" in amber read as an unmet requirement and made a
                          perfectly valid grid look broken. The plain count of who is picked is
                          already visible in the chips and in the footer total. */}
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-[12.5px] font-medium text-slate-600">{relationLabel(r.relation)}</span>
                        <span className="text-[11.5px] text-slate-400">น้ำหนัก {r.rule.weight_percent}%</span>
                        {n > 0 && (
                          <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ${TONE_CLASS.emerald}`}>
                            เลือก {n}
                          </span>
                        )}
                      </div>
                      {!r.candidates.length ? (
                        <p className="text-[12.5px] text-slate-400">
                          {r.relation === 'doctor'
                            ? 'ไม่พบทันตแพทย์ที่ลงตารางที่สาขานี้ในช่วงเวลาที่ประเมิน'
                            : 'ไม่พบผู้ประเมินที่ตรงเงื่อนไข'}
                        </p>
                      ) : r.relation === 'doctor' ? (
                        // Dozens of candidates — a row of chips that long is unreadable and
                        // impossible to aim at. The app's standard picker for "a list too
                        // long to be chips", with its own search. The CHOSEN ones are then
                        // shown as chips underneath: "เลือกแล้ว 1" tells you a count but not
                        // who, and who is the thing being checked.
                        <div>
                          <div className="max-w-md">
                            <SearchableSelect
                              multiple
                              options={r.candidates.map((c) => ({ value: chipKey(c), label: c.label, hint: c.hint }))}
                              values={r.selected}
                              onChange={(next) => setCell(si, ri, next)}
                              placeholder="เลือกทันตแพทย์…"
                              searchPlaceholder="พิมพ์ชื่อหมอเพื่อค้นหา…"
                              emptyText="ไม่พบทันตแพทย์"
                            />
                          </div>
                          {n > 0 && (
                            <div className="flex flex-wrap gap-1.5 mt-2">
                              {r.candidates.filter((c) => r.selected.has(chipKey(c))).map((c) => (
                                <Chip
                                  key={chipKey(c)}
                                  c={c}
                                  on
                                  onClick={() => toggleChip(si, ri, chipKey(c), r.selected)}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      ) : r.relation === 'peer' ? (
                        // Peers can span several positions (a Counter's peers are Counter AND
                        // ผู้ช่วยทันตแพทย์), so bucket them and let a whole position be added
                        // in one click — that is how the choice is actually made.
                        <div className="space-y-2">
                          {groupChips(r.candidates).map(([groupName, chips]) => {
                            const nOn = chips.filter((c) => r.selected.has(chipKey(c))).length
                            const allOn = nOn === chips.length
                            return (
                              <div key={groupName}>
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-[11.5px] font-medium text-slate-500">{groupName}</span>
                                  <span className="text-[11px] text-slate-400 tabular-nums">{nOn}/{chips.length}</span>
                                  <button
                                    type="button"
                                    onClick={() => toggleChipGroup(si, ri, r.selected, chips, !allOn)}
                                    className="text-[11.5px] font-medium text-brand-700 hover:underline"
                                  >
                                    {allOn ? 'เอาออกทั้งกลุ่ม' : 'เลือกทั้งกลุ่ม'}
                                  </button>
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                  {chips.map((c) => (
                                    <Chip
                                      key={chipKey(c)}
                                      c={c}
                                      on={r.selected.has(chipKey(c))}
                                      onClick={() => toggleChip(si, ri, chipKey(c), r.selected)}
                                    />
                                  ))}
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-1.5">
                          {r.candidates.map((c) => (
                            <Chip
                              key={chipKey(c)}
                              c={c}
                              on={r.selected.has(chipKey(c))}
                              onClick={() => toggleChip(si, ri, chipKey(c), r.selected)}
                            />
                          ))}
                        </div>
                      )}

                    </div>
                  )
                })}

                {/* กำหนดเอง — always available, no setup. One picker per subject that adds
                    people the rules never proposed (typically another branch). They join the
                    เพื่อนร่วมงาน row rather than forming a group of their own: a custom group
                    would need its own question set and its own weight, and there is no honest
                    default for either. See customTargetIndex() in lib/evaluation.js. */}
                {(() => {
                  const ti = customTargetIndex(row.rows)
                  if (ti < 0) return null
                  const target = row.rows[ti]
                  const manualKeys = new Set(
                    target.candidates.filter((c) => c.manual).map(chipKey),
                  )
                  const taken = new Set()
                  row.rows.forEach((r) => r.candidates.forEach((c) => {
                    if (!c.manual) taken.add(chipKey(c))
                  }))
                  return (
                    <div className="px-5 py-3 border-t border-slate-100 bg-slate-50/40 rounded-b-2xl">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-[12.5px] font-medium text-slate-600">กำหนดเอง</span>
                        <span className="text-[11.5px] text-slate-400">
                          เพิ่มคนที่ไม่ได้อยู่ในเงื่อนไข · นับรวมกับ{relationLabel(target.relation)} (น้ำหนัก {target.rule.weight_percent}%)
                        </span>
                        {manualKeys.size > 0 && (
                          <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ${TONE_CLASS.violet}`}>
                            {manualKeys.size} คน
                          </span>
                        )}
                      </div>
                      <div className="max-w-md">
                        <SearchableSelect
                          multiple
                          options={employees
                            .filter((e) => e.active !== false
                              && e.id !== row.subject.id
                              && !taken.has(`employee:${e.id}`))
                            .map((e) => ({
                              value: `employee:${e.id}`,
                              label: e.code || e.nickname || e.name || `#${e.id}`,
                              hint: `${e.position || '—'} · ${branchNameOf(e.branch_id)}`,
                            }))}
                          values={manualKeys}
                          onChange={(next) => setManualFor(si, ti, next)}
                          placeholder="+ เพิ่มผู้ประเมินเอง (ข้ามสาขาได้)"
                          searchPlaceholder="พิมพ์ชื่อ / ตำแหน่ง / สาขา…"
                          emptyText="ไม่พบพนักงานที่ยังไม่ได้อยู่ในรายการ"
                        />
                      </div>
                    </div>
                  )
                })()}
              </div>
            ))}
          </div>

          <div className="sticky bottom-0 mt-4 -mx-10 px-10 py-4 bg-white/90 backdrop-blur border-t border-slate-200 flex items-center justify-between gap-4">
            <div className="text-[13.5px] text-slate-600">
              จะส่งแบบประเมิน <span className="font-semibold text-slate-900">{totals.papers}</span> ชุด
              ให้ผู้ประเมิน <span className="font-semibold text-slate-900">{totals.people}</span> คน
            </div>
            <button
              type="button"
              disabled={!totals.papers || sending}
              onClick={() => setConfirm(true)}
              className="px-4 py-2.5 rounded-xl text-[14px] font-semibold text-white bg-brand-600 hover:bg-brand-700 shadow-sm disabled:opacity-40 inline-flex items-center gap-2"
            >
              {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} strokeWidth={1.75} />}
              ส่งแบบประเมิน
            </button>
          </div>
        </>
      )}

      {confirm && (
        <ConfirmDialog
          title="ส่งแบบประเมิน?"
          message={`จะสร้างแบบประเมิน ${totals.papers} ชุด ให้ผู้ประเมิน ${totals.people} คน และเปิดรอบประเมินทันที`}
          confirmLabel="ส่งเลย"
          onConfirm={dispatch}
          onCancel={() => setConfirm(false)}
        />
      )}
    </div>
  )
}
